import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  AccordionDetails,
  Accordion,
  AccordionSummary,
  InputLabel,
  FormControlLabel,
  Radio,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RadioGroup, TextField } from "formik-material-ui";
import useSharedStyles from "../../styles/Shared";
import useButtonStyles from "../../styles/Buttons";
import { COLORS } from "../../themes/Base";
import { Field, Formik } from "formik";
import SupplierQuestions from "../../components/Suppliers/Questions/SupplierQuestions";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoCompleteDropdown from "../../themes/components/AutoCompleteDropdown";
import { countryCodes } from "../../types/countryCodes";
import * as Yup from "yup";
import CustomCheckbox from "../../components/Form/CustomCheckbox";
import { updateProjectSupplier } from "../../api/Project";
import { isEmpty } from "lodash";
import { notify, notifySimple } from "../../themes/components/Notifications";

const validationSchema = Yup.object().shape({
  input: Yup.object().shape({
    score: Yup.number()
      .min(20, "Score Threshold must be at least 20")
      .max(100, "Score Threshold must be at most 100")
      .required("Score Threshold is required"),
    rt_proof_of_human_threshold: Yup.number()
      .min(20, "Roundtable AI Proof of Human Score Threshold must be at least 20")
      .max(100, "Roundtable AI Proof of Human Score Threshold must be at most 100")
      .required("Roundtable AI Proof of Human Score Threshold is required"),
  }),
});

type EditSupplierFormModalProps = {
  open: boolean;
  onClose: () => void;
  projectSupplier: ProjectSupplier | null;
  projectId: string;
  refetch: () => void;
};

const EditSupplierFormModal: React.FC<EditSupplierFormModalProps> = ({
  open,
  onClose,
  projectSupplier,
  projectId,
  refetch,
}) => {
  if (!projectSupplier) return null;

  const [innerExpanded, setInnerExpanded] = useState<string | false>(false);
  const { classes } = useSharedStyles();
  const { classes: buttonClasses, cx } = useButtonStyles();

  const onSubmit = async (values: ProjectSupplierVars) => {
    try {
      const response = await updateProjectSupplier(projectId, projectSupplier.id, values);
      const { result, messages } = response.projectSupplierUpdate;

      if (!isEmpty(messages)) {
        notify(messages, "error");
      } else if (result) {
        notifySimple(["Project supplier updated"], "success");
        refetch();
        onClose();
      }
    } catch (err) {
      notifySimple(["Error updating project supplier"], "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        className: classes.dialogModalBox,
      }}
    >
      <Box
        sx={{
          bgcolor: "#FAFAF2",
          px: 3,
          py: 2,
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E4E8D3",
        }}
      >
        <Typography variant="h3" sx={{ p: 0 }}>
          {projectSupplier.source.name}
          <span style={{ color: COLORS.textSecondary }}>&nbsp;{projectSupplier.country_code}</span>
        </Typography>
        <IconButton onClick={onClose} sx={{ color: COLORS.textSecondary }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 3, bgcolor: "#FFFFFD" }}>
        <Formik<ProjectSupplierVars>
          onSubmit={onSubmit}
          initialValues={{
            id: projectSupplier.id,
            input: {
              description: projectSupplier.description || "",
              country_code: projectSupplier.country_code || "", // The geo IP ISO-2 country code associated with the device
              survey_url: projectSupplier.survey_url,
              limit: projectSupplier.limit,
              score: projectSupplier.score || 0,
              rt_proof_of_human_threshold: projectSupplier.rt_proof_of_human_threshold ?? 60,
              opening_tag: projectSupplier.opening_tag,
              closing_tag: projectSupplier.closing_tag,
              respondent_key: projectSupplier.respondent_key || "",
              status_key: projectSupplier.status_key || "",
              supplier_id: projectSupplier.source.id,
              questions: projectSupplier.questions,
              pre_survey_tool: projectSupplier.pre_survey_tool || "",
              pre_survey_id: projectSupplier.pre_survey_id || "",
              ai_validation: projectSupplier.ai_validation || false,
              fields: projectSupplier.fields.map((field) => ({
                id: field.id,
                name: field.name,
                value: field.value,
                required: field.required,
                supplier_field_id: field.source?.id,
              })),
            },
          }}
          validationSchema={validationSchema}
        >
          {({ values, setFieldValue, handleSubmit }) => (
            <Box sx={{ pb: 10 }}>
              <Box width="100%">
                <Box className={classes.fieldBox}>
                  <InputLabel>Description</InputLabel>
                  <Field
                    component={TextField}
                    name={`input.description`}
                    variant="outlined"
                    fullWidth
                    style={{ marginRight: 8 }}
                    placeholder="Description"
                  />
                </Box>
                <Box className={classes.fieldBox}>
                  <InputLabel>Country Code</InputLabel>
                  <AutoCompleteDropdown
                    name="input.country_code"
                    label="Country Code"
                    value={countryCodes.find(cc => cc.code === projectSupplier.country_code) || ""}
                    options={countryCodes}
                    onChange={(code) => setFieldValue(`input.country_code`, code)}
                    getOptionLabel={(option) => `${option.name} - Country Code: ${option.code}`}
                    getOptionValue={(option) => option.code}
                    placeholder="Country Code"
                  />
                </Box>
                <Box className={classes.fieldBox}>
                  <InputLabel>Survey base URL</InputLabel>
                  <Field
                    component={TextField}
                    name={`input.survey_url`}
                    variant="outlined"
                    fullWidth
                    style={{ marginRight: 8 }}
                    placeholder="Survey base URL"
                  />
                </Box>
                <Box className={classes.fieldBox}>
                  <InputLabel>Responses limit</InputLabel>
                  <Field
                    component={TextField}
                    name={`input.limit`}
                    variant="outlined"
                    type="number"
                    fullWidth
                  />
                </Box>
                <Box className={classes.fieldBox}>
                  <InputLabel>Device Forensiq Score Threshold</InputLabel>
                  <Field
                    component={TextField}
                    name={`input.score`}
                    variant="outlined"
                    type="number"
                    // value={projectSupplier.score}
                    fullWidth
                  />
                </Box>
                <Box className={classes.fieldBox}>
                  <InputLabel>Roundtable AI Proof of Human Score Threshold</InputLabel>
                  <Field
                    component={TextField}
                    name={`input.rt_proof_of_human_threshold`}
                    variant="outlined"
                    type="number"
                    // value={projectSupplier.score}
                    fullWidth
                  />
                </Box>
                <div>
                  <input
                    type="hidden"
                    name={`input.supplier_id`}
                    value={projectSupplier.supplier_id}
                  />
                </div>
                <Accordion
                  expanded={innerExpanded === "url"}
                  onChange={() =>
                    setInnerExpanded(innerExpanded === "url" ? false : "url")
                  }
                  sx={{
                    mb: 2,
                    boxShadow: "none",
                    borderRadius: 2,
                    border: "1px solid #E4E8D3",
                    "& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content": {
                      margin: 0,
                    },
                  }}
                >
                  <AccordionSummary sx={{ padding: "16px 24px" }}>
                    <Typography variant="h3" sx={{ p: 0 }}>URL Requirements</Typography>
                    <ExpandMoreIcon
                      sx={{
                        transition: "transform 0.2s",
                        transform: innerExpanded === "url" ? "rotate(180deg)" : "rotate(0deg)",
                        ml: 1,
                      }}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box px={1.2}>
                      Survey redirect URLs dynamic placeholders markup
                      <Typography variant="subtitle1">
                        These should be provided by the Survey Tool to wrap any dynamic URL variable,
                        for example <code>{"{{RID}}"}</code>
                      </Typography>
                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Box className={classes.fieldBox} width="100%">
                          <InputLabel>Opening tag</InputLabel>
                          <Field
                            component={TextField}
                            name={`input.opening_tag`}
                            variant="outlined"
                            fullWidth
                          />
                        </Box>
                        <Box className={classes.fieldBox} width="100%">
                          <InputLabel>Closing tag</InputLabel>
                          <Field
                            component={TextField}
                            name={`input.closing_tag`}
                            variant="outlined"
                            fullWidth
                          />
                        </Box>
                      </Box>
                      <Typography variant="h3" sx={{ py: 2 }}>URL Variable Mapping</Typography>
                      <Box display="flex" justifyContent="space-between" gap={2}>
                        <Box className={classes.fieldBox} width="100%">
                          <InputLabel>obsrv_id</InputLabel>
                          <Field
                            component={TextField}
                            name={`input.respondent_key`}
                            variant="outlined"
                            fullWidth
                          />
                        </Box>
                        <Box className={classes.fieldBox} width="100%">
                          <InputLabel>obsrv_v</InputLabel>
                          <Field
                            component={TextField}
                            name={`input.status_key`}
                            variant="outlined"
                            fullWidth
                          />
                        </Box>
                      </Box>
                      <Typography variant="h3" sx={{ py: 2 }}>Fields</Typography>
                      {projectSupplier.fields.map((supplier_field) => (
                        <div key={supplier_field.id}>
                          <FormControlLabel
                            disabled={supplier_field.source.required && !supplier_field?.deleted}
                            label={supplier_field.source.name || ""}
                            control={
                              <CustomCheckbox
                                sx={{ py: 0 }}
                                color="secondary"
                                checked={supplier_field?.source?.required && !supplier_field?.deleted}
                              />
                            }
                          />
                        </div>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
                {projectSupplier.id && (
                  <Accordion
                    expanded={innerExpanded === "pre"}
                    onChange={() => setInnerExpanded(innerExpanded === "pre" ? false : "pre")}
                    sx={{
                      mb: 2,
                      boxShadow: "none",
                      borderRadius: 2,
                      border: "1px solid #E4E8D3",
                      "& .MuiAccordionSummary-content.Mui-expanded, & .MuiAccordionSummary-content": {
                        margin: 0,
                      },
                    }}
                  >
                    <AccordionSummary sx={{ padding: "16px 24px" }}>
                      <Typography variant="h3" sx={{ p: 0 }}>Pre-Survey Questions</Typography>
                      <ExpandMoreIcon
                        sx={{
                          transition: "transform 0.2s",
                          transform: innerExpanded === "pre" ? "rotate(180deg)" : "rotate(0deg)",
                          ml: 1,
                        }}
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box px={1.2}>
                        <Box mb={1}>Pre Survey Questions</Box>
                        <Box className={classes.fieldBox} width="100%">
                          <InputLabel>Pre-Survey Id</InputLabel>
                          <Field
                            component={TextField}
                            name={`input.pre_survey_id`}
                            placeholder="Pre-Survey Id"
                            variant="outlined"
                            type="text"
                            fullWidth
                          />
                        </Box>
                        <Box className={classes.fieldBox} width="100%">
                          <InputLabel>Pre-Survey Tool</InputLabel>
                          <Field
                            component={RadioGroup}
                            name={`input.pre_survey_tool`}
                            row
                            label="Pre-Survey Tool"
                          >
                            <FormControlLabel
                              value=""
                              control={<Radio color="secondary" />}
                              label="None"
                            />
                            <FormControlLabel
                              value="roundtable"
                              control={<Radio color="secondary" />}
                              label="Roundtable AI"
                            />
                          </Field>
                        </Box>
                        {values.input.pre_survey_tool === "roundtable" && (
                          <Box className={classes.fieldBox} width="100%" ml={1}>
                            <FormControlLabel
                              control={
                                <CustomCheckbox
                                  sx={{ py: 0 }}
                                  name={`input.ai_validation`}
                                  checked={values.input.ai_validation ?? false}
                                  onChange={() =>
                                    setFieldValue(
                                      `input.ai_validation`,
                                      !values.input.ai_validation
                                    )
                                  }
                                />
                              }
                              label="AI validation"
                            />
                          </Box>
                        )}
                        {values.input.pre_survey_tool && (
                          <SupplierQuestions
                            questions={values.input.questions || []}
                            name={`input.questions`}
                            updateQuestions={(
                              inputName: string,
                              questions: ProjectSupplierQuestion[]
                            ) => {
                              setFieldValue(inputName, questions);
                            }}
                          />
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  bgcolor: "#FFFFFD",
                  borderTop: "1px solid #E4E8D3",
                  zIndex: 10,
                  px: 3,
                  py: 2,
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className={cx(buttonClasses.grey, buttonClasses.iconButton)}
                  style={{ marginRight: 8 }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleSubmit()}
                  className={cx(buttonClasses.blue, buttonClasses.iconButton)}
                >
                  Save changes
                </Button>
              </Box>
            </Box>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default EditSupplierFormModal;
